import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import {
    Brain,
    LayoutDashboard,
    Users,
    BarChart3,
    Heart,
    AlertTriangle,
    Mic,
    FileText,
    Settings,
    LogOut,
    ChevronRight,
    User,
    Building2,
    Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const platformOwnerNavItems = [
    { path: '/platform', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/platform', label: 'Organizations', icon: Building2, tab: 'organizations' },
    { path: '/platform', label: 'Analytics', icon: BarChart3, tab: 'analytics' },
];

const hrNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/employees', label: 'Employees', icon: Users },
    { path: '/wellbeing', label: 'Well-being', icon: Heart },
    { path: '/alerts', label: 'Alerts', icon: AlertTriangle },
    { path: '/voice', label: 'Voice Analysis', icon: Mic },
    { path: '/surveys', label: 'Surveys', icon: FileText },
];

const employeeNavItems = [
    { path: '/employee', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/employee/wellbeing', label: 'Well-being', icon: Heart },
    { path: '/employee/psychometrics', label: 'My Profile', icon: User },
    { path: '/employee/surveys', label: 'Surveys', icon: FileText },
];

export default function Layout({ children }) {
    const { user, logout, isHR } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const isPlatformOwner = user?.role === 'platform_owner';
    const navItems = isPlatformOwner ? platformOwnerNavItems : (isHR() ? hrNavItems : employeeNavItems);
    const homeLink = isPlatformOwner ? '/platform' : (isHR() ? '/dashboard' : '/employee');

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // For platform owner, use a special layout without sidebar
    if (isPlatformOwner) {
        return (
            <div className="min-h-screen bg-gray-50">
                {children}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAF9]">
            {/* Sidebar */}
            <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-stone-200 z-40">
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 border-b border-stone-100">
                        <Link to={homeLink} className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-glow">
                                <Brain className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xl font-bold font-heading text-stone-900">EmpathIQ</span>
                        </Link>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                                    className={cn(
                                        'flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200',
                                        isActive
                                            ? 'bg-indigo-50 text-indigo-600 font-medium'
                                            : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                                    )}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span>{item.label}</span>
                                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User Menu */}
                    <div className="p-4 border-t border-stone-100">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start gap-3 h-auto py-3 px-4 hover:bg-stone-50"
                                    data-testid="user-menu-btn"
                                >
                                    <Avatar className="w-9 h-9">
                                        <AvatarFallback className="bg-indigo-100 text-indigo-600 font-medium">
                                            {getInitials(user?.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-stone-900">{user?.name || 'User'}</p>
                                        <p className="text-xs text-stone-500 capitalize">{user?.role?.replace('_', ' ') || 'Employee'}</p>
                                    </div>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                <DropdownMenuItem className="cursor-pointer">
                                    <Settings className="w-4 h-4 mr-2" />
                                    Settings
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    className="cursor-pointer text-red-600 focus:text-red-600"
                                    onClick={handleLogout}
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Log Out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="ml-64 min-h-screen">
                {children}
            </main>
        </div>
    );
}
